import { useEffect, useState } from 'react'
import { LabeledValueType } from 'components/CIMSSelect/types'
import {
  GroupLevelMap,
  TwoDimensionalGroupFormItemsProps,
  newGroupLevelMap,
} from './types'
import {
  Group,
  useFDCNotificationsContext,
} from '../../../../../../FDCNotificationsContext'
import CIMSFormItem from 'components/CIMSFormItem'
import RecipientsSelect from 'components/CIMSSelect/common/RecipientsSelect'
import ApproverTags from '../../../../../../components/ApproverTags'
import ConcurrerTags from '../../../../../../components/ConcurrerTags'
import { WithLabelledRecipients } from '../../../../types'
import GroupFallback from '../../../../../../components/GroupFallback'
import { HelpMessage } from 'utils/constants'
import { useCIMSNotificationsPanelContext } from 'components/CIMSNotifications/components/CIMSNotificationsPanelContext'
import CIMSCollapse from 'components/CIMSCollapse'
import CIMSClearButton from 'components/CIMSClearButton'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { Space } from 'antd'
import CIMSSection from 'components/CIMSSection'

const TwoDimensionalGroupFormItems: React.FC<
  TwoDimensionalGroupFormItemsProps
> = ({ onChange, value, group }) => {
  const { event } = useCIMSNotificationsPanelContext()
  const help = HelpMessage.FieldDesignChanges.Notifications
  const { data, approverEvents, concurrerEvents } = useFDCNotificationsContext()
  const groupData = data
    ? ((group === 'Approval Group'
        ? data.fdcApprovalGroups
        : data.fdcConcurrenceGroups) as Group[])
    : []
  type GroupMap = Map<string, { groupId: string } & WithLabelledRecipients>
  type Values = { groups: GroupMap; groupsLevels: GroupLevelMap }
  const [values, setValues] = useState<Values>(
    value
      ? {
          groups:
            value.type === 'approvalGroup'
              ? value.approvalGroups.reduce(
                  (map, { approvalGroupId: groupId, ...rest }) =>
                    map.set(groupId, { ...rest, groupId }),
                  new Map()
                )
              : value.concurrenceGroups.reduce(
                  (map, { concurrenceGroupId: groupId, ...rest }) =>
                    map.set(groupId, { ...rest, groupId }),
                  new Map()
                ),
          groupsLevels:
            value.type === 'approvalGroup'
              ? value.approvalGroupConcurrenceLevels.reduce(
                  (map, { approvalGroupId: groupId, ...rest }) =>
                    map.set({ ...rest, groupId }),
                  newGroupLevelMap()
                )
              : value.concurrenceGroupConcurrenceLevels.reduce(
                  (map, { concurrenceGroupId: groupId, ...rest }) =>
                    map.set({ ...rest, groupId }),
                  newGroupLevelMap()
                ),
        }
      : { groups: new Map(), groupsLevels: newGroupLevelMap() }
  )
  useEffect(() => {
    const { groups, groupsLevels } = values
    const byGroup = [...groups.values()]
    const byGroupLevel = [...groupsLevels.values()]
    onChange?.(
      group === 'Approval Group'
        ? {
            type: 'approvalGroup',
            approvalGroups: byGroup.map(({ groupId, ...rest }) => ({
              ...rest,
              approvalGroupId: groupId,
            })),
            approvalGroupConcurrenceLevels: byGroupLevel.map(
              ({ groupId, ...rest }) => ({
                ...rest,
                approvalGroupId: groupId,
              })
            ),
          }
        : {
            type: 'concurrenceGroup',
            concurrenceGroups: byGroup.map(({ groupId, ...rest }) => ({
              ...rest,
              concurrenceGroupId: groupId,
            })),
            concurrenceGroupConcurrenceLevels: byGroupLevel.map(
              ({ groupId, ...rest }) => ({
                ...rest,
                concurrenceGroupId: groupId,
              })
            ),
          }
    )
    // do not include onChange to avoid infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, values])
  const [visible, { add, remove }] = useKeyedBooleans<string>()
  useEffect(() => {
    const { groups } = values
    const groupLevels = [...values.groupsLevels.values()]
    groupData.forEach(({ id }) => {
      remove(id)
      const groupHasRecipients = !!groups.get(id)?.recipients?.length
      const groupLevelsHasRecipients = groupLevels.some(
        ({ groupId, recipients }) => groupId === id && !!recipients?.length
      )
      if (groupHasRecipients || groupLevelsHasRecipients) add(id)
    })
    // do not listen to add/remove -> infinite re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, groupData])
  const groups = `${group}s` as const
  return (
    <CIMSSection
      help={
        group === 'Approval Group'
          ? help.ApprovalGroups(event)
          : help.ConcurrenceGroups(event)
      }
      title={groups}
    >
      <CIMSCollapse
        noPanelsContent={<GroupFallback group={groups} />}
        panels={groupData.map(({ id: groupId, name, description }) => ({
          key: groupId,
          header: {
            type: 'described',
            name,
            description,
          },
          extra: visible(groupId)
            ? {
                type: 'default',
                children: (
                  <CIMSClearButton
                    onClick={() =>
                      setValues({
                        groups: new Map(),
                        groupsLevels: newGroupLevelMap(),
                      })
                    }
                  />
                ),
              }
            : undefined,
          children: (
            <>
              {group === 'Approval Group' &&
                approverEvents.some(
                  (approverEvent) => approverEvent.id === event.id
                ) && <ApproverTags approvalGroup={{ id: groupId, name }} />}
              {group === 'Concurrence Group' &&
                concurrerEvents.some(
                  (concurrerEvent) => concurrerEvent.id === event.id
                ) && <ConcurrerTags concurrenceGroup={{ id: groupId, name }} />}
              <CIMSSection title={group} help={help.Group(name, event)}>
                <CIMSFormItem label="Recipients">
                  <RecipientsSelect
                    value={values.groups.get(groupId)?.recipients ?? []}
                    onChange={(value) =>
                      setValues((values) => {
                        const next = { ...values }
                        next.groups.set(groupId, {
                          groupId,
                          recipients: value as LabeledValueType[],
                        })
                        return next
                      })
                    }
                  />
                </CIMSFormItem>
              </CIMSSection>
              <CIMSSection
                help={help.GroupLevel(name, event)}
                title="Concurrence Levels"
              >
                <CIMSCollapse
                  noPanelsContent={<GroupFallback group="Concurrence Levels" />}
                  panels={(data?.fdcConcurrenceLevels ?? []).map(
                    ({ id: concurrenceLevelId, name, description }) => ({
                      key: concurrenceLevelId,
                      header: {
                        type: 'described',
                        name: name,
                        description: description,
                      },
                      children: (
                        <Space
                          size="large"
                          direction="vertical"
                          style={{ width: '100%' }}
                        >
                          <ConcurrerTags
                            concurrenceGroup={{
                              id: concurrenceLevelId,
                              name,
                            }}
                          />
                          <CIMSFormItem label="Recipients">
                            <RecipientsSelect
                              value={
                                values.groupsLevels.get({
                                  groupId,
                                  concurrenceLevelId,
                                })?.recipients ?? []
                              }
                              onChange={(value) =>
                                setValues((values) => {
                                  const next = { ...values }
                                  next.groupsLevels.set({
                                    concurrenceLevelId,
                                    groupId,
                                    recipients: value as LabeledValueType[],
                                  })
                                  return next
                                })
                              }
                            />
                          </CIMSFormItem>
                        </Space>
                      ),
                    })
                  )}
                />
              </CIMSSection>
            </>
          ),
        }))}
      />
    </CIMSSection>
  )
}

export default TwoDimensionalGroupFormItems
